import React, { useRef, useReducer, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { Modal, Button, Space, Slider, Input, ColorPicker, Upload, Tabs, message, Row, Col, Card, Select, Divider, Tooltip, Spin } from 'antd';
import {
    CheckOutlined, CloseOutlined, FontSizeOutlined, BgColorsOutlined, PictureOutlined,
    ScissorOutlined, FilterOutlined, UndoOutlined, RedoOutlined, DeleteOutlined,
    RotateLeftOutlined, RotateRightOutlined, BoldOutlined, ItalicOutlined, UnderlineOutlined,
    InstagramOutlined, FacebookOutlined, TwitterOutlined, LinkedinOutlined,
    ZoomInOutlined, ZoomOutOutlined, ExpandOutlined, ShrinkOutlined,
    LeftOutlined, RightOutlined
} from '@ant-design/icons';
import { useTranslation, Trans } from 'react-i18next';
import { Stage, Layer, Image, Text, Transformer, Rect } from 'react-konva';
import Konva from 'konva';

const FILTER_PRESETS = {
    none: { nameKey: 'imageEditor.filters.none', brightness: 0, contrast: 0, saturation: 0, sepia: 0, grayscale: 0, blur: 0, hue: 0 },
    clarendon: { nameKey: 'imageEditor.filters.clarendon', brightness: 10, contrast: 20, saturation: 15, sepia: 0, grayscale: 0, blur: 0, hue: 0 },
    gingham: { nameKey: 'imageEditor.filters.gingham', brightness: 5, contrast: -10, saturation: -20, sepia: 0, grayscale: 0, blur: 0, hue: 350 },
    moon: { nameKey: 'imageEditor.filters.moon', brightness: 10, contrast: 10, saturation: -100, sepia: 0, grayscale: 100, blur: 0, hue: 0 },
    lark: { nameKey: 'imageEditor.filters.lark', brightness: 15, contrast: -10, saturation: 20, sepia: 0, grayscale: 0, blur: 0, hue: 0 },
    reyes: { nameKey: 'imageEditor.filters.reyes', brightness: 10, contrast: -15, saturation: 30, sepia: 20, grayscale: 0, blur: 0, hue: 0 },
    juno: { nameKey: 'imageEditor.filters.juno', brightness: 10, contrast: 15, saturation: 40, sepia: 0, grayscale: 0, blur: 0, hue: 10 },
    slumber: { nameKey: 'imageEditor.filters.slumber', brightness: 5, contrast: -5, saturation: 20, sepia: 10, grayscale: 0, blur: 0, hue: 0 },
    crema: { nameKey: 'imageEditor.filters.crema', brightness: 5, contrast: 10, saturation: -10, sepia: 15, grayscale: 0, blur: 0, hue: 0 },
    ludwig: { nameKey: 'imageEditor.filters.ludwig', brightness: 10, contrast: 15, saturation: 10, sepia: 5, grayscale: 0, blur: 0, hue: 0 },
    aden: { nameKey: 'imageEditor.filters.aden', brightness: 10, contrast: -10, saturation: -20, sepia: 0, grayscale: 0, blur: 0, hue: 340 },
    amaro: { nameKey: 'imageEditor.filters.amaro', brightness: 10, contrast: 10, saturation: 30, sepia: 0, grayscale: 0, blur: 0, hue: 0 },
    mayfair: { nameKey: 'imageEditor.filters.mayfair', brightness: 10, contrast: 10, saturation: 15, sepia: 5, grayscale: 0, blur: 0, hue: 0 },
    rise: { nameKey: 'imageEditor.filters.rise', brightness: 15, contrast: 10, saturation: 20, sepia: 10, grayscale: 0, blur: 0, hue: 0 },
    hudson: { nameKey: 'imageEditor.filters.hudson', brightness: 10, contrast: 10, saturation: 20, sepia: 0, grayscale: 0, blur: 0, hue: 0 },
    valencia: { nameKey: 'imageEditor.filters.valencia', brightness: 10, contrast: 5, saturation: 10, sepia: 10, grayscale: 0, blur: 0, hue: 5 },
    xpro2: { nameKey: 'imageEditor.filters.xpro2', brightness: 5, contrast: 25, saturation: 30, sepia: 10, grayscale: 0, blur: 0, hue: 0 },
    sierra: { nameKey: 'imageEditor.filters.sierra', brightness: 10, contrast: 15, saturation: 20, sepia: 5, grayscale: 0, blur: 0, hue: 0 },
    willow: { nameKey: 'imageEditor.filters.willow', brightness: 5, contrast: 5, saturation: -100, sepia: 0, grayscale: 50, blur: 0, hue: 0 },
    lofi: { nameKey: 'imageEditor.filters.lofi', brightness: 5, contrast: 30, saturation: 20, sepia: 0, grayscale: 0, blur: 0, hue: 0 },
    inkwell: { nameKey: 'imageEditor.filters.inkwell', brightness: 10, contrast: 10, saturation: -100, sepia: 0, grayscale: 100, blur: 0, hue: 0 },
    nashville: { nameKey: 'imageEditor.filters.nashville', brightness: 10, contrast: 10, saturation: 20, sepia: 20, grayscale: 0, blur: 0, hue: 10 },
};

const SOCIAL_PRESETS = {
    instagram: {
        nameKey: 'imageEditor.social.instagram.name',
        width: 1080,
        height: 1080,
        icon: <InstagramOutlined />,
        descriptionKey: 'imageEditor.social.instagram.description'
    },
    instagram_story: {
        nameKey: 'imageEditor.social.instagramStory.name',
        width: 1080,
        height: 1920,
        icon: <InstagramOutlined />,
        descriptionKey: 'imageEditor.social.instagramStory.description'
    },
    facebook: {
        nameKey: 'imageEditor.social.facebook.name',
        width: 1200,
        height: 630,
        icon: <FacebookOutlined />,
        descriptionKey: 'imageEditor.social.facebook.description'
    },
    threads: {
        nameKey: 'imageEditor.social.threads.name',
        width: 1080,
        height: 1350,
        icon: <TwitterOutlined />,
        descriptionKey: 'imageEditor.social.threads.description'
    },
    linkedin: {
        nameKey: 'imageEditor.social.linkedin.name',
        width: 1200,
        height: 627,
        icon: <LinkedinOutlined />,
        descriptionKey: 'imageEditor.social.linkedin.description'
    },
};

const CROP_PRESETS = {
    free: { nameKey: 'imageEditor.crop.free', aspect: undefined },
    square: { nameKey: 'imageEditor.crop.square', aspect: 1 },
    instagram: { nameKey: 'imageEditor.crop.instagram', aspect: 4 / 5 },
    story: { nameKey: 'imageEditor.crop.story', aspect: 9 / 16 },
    landscape: { nameKey: 'imageEditor.crop.landscape', aspect: 16 / 9 },
    portrait: { nameKey: 'imageEditor.crop.portrait', aspect: 3 / 4 },
};

const initialState = {
    image: null,
    imageSize: { width: 0, height: 0 },
    texts: [],
    stickers: [],
    selectedId: null,
    adjustments: {
        brightness: 0, contrast: 0, saturation: 0, sepia: 0, grayscale: 0, blur: 0, hue: 0
    },
    cropMode: false,
    cropRect: { x: 10, y: 10, width: 300, height: 200 },
    cropAspect: undefined,
    selectedFilter: 'none',
    history: [],
    historyStep: -1,
    rotation: 0,
};

function editorReducer(state, action) {
    switch (action.type) {
        case 'LOAD_IMAGE':
            const loadedImage = action.payload;
            const newState = {
                ...initialState,
                image: loadedImage,
                imageSize: { width: loadedImage.width, height: loadedImage.height },
                cropRect: {
                    x: loadedImage.width * 0.1,
                    y: loadedImage.height * 0.1,
                    width: loadedImage.width * 0.8,
                    height: loadedImage.height * 0.8
                }
            };
            return {
                ...newState,
                history: [newState],
                historyStep: 0,
            };

        case 'SET_ADJUSTMENT':
            return {
                ...state,
                adjustments: { ...state.adjustments, ...action.payload },
            };

        case 'ADD_TEXT':
            return {
                ...state,
                texts: [...state.texts, action.payload],
                selectedId: action.payload.id
            };

        case 'ADD_STICKER':
            return {
                ...state,
                stickers: [...state.stickers, action.payload],
                selectedId: action.payload.id
            };

        case 'SET_SELECTED':
            return { ...state, selectedId: action.payload };

        case 'UPDATE_OBJECT_PROPS':
            const { id, props } = action.payload;
            return {
                ...state,
                texts: state.texts.map(t => t.id === id ? { ...t, ...props } : t),
                stickers: state.stickers.map(s => s.id === id ? { ...s, ...props } : s),
            };

        case 'DELETE_SELECTED':
            if (!state.selectedId) return state;
            return {
                ...state,
                texts: state.texts.filter(t => t.id !== state.selectedId),
                stickers: state.stickers.filter(s => s.id !== state.selectedId),
                selectedId: null,
            };

        case 'TOGGLE_CROP_MODE':
            const newCropMode = !state.cropMode;
            return {
                ...state,
                cropMode: newCropMode,
                selectedId: null,
                cropRect: newCropMode ? {
                    x: state.imageSize.width * 0.1,
                    y: state.imageSize.height * 0.1,
                    width: state.imageSize.width * 0.8,
                    height: state.imageSize.height * 0.8
                } : state.cropRect
            };

        case 'UPDATE_CROP_RECT':
            return { ...state, cropRect: { ...state.cropRect, ...action.payload } };

        case 'SET_CROP_ASPECT':
            return {
                ...state,
                cropAspect: action.payload.aspect,
                cropRect: action.payload.rect,
                cropMode: true
            };

        case 'APPLY_CROP':
            const { newSize, newTexts, newStickers, newImage: croppedImage } = action.payload;
            return {
                ...state,
                image: croppedImage,
                imageSize: newSize,
                texts: newTexts,
                stickers: newStickers,
                cropMode: false,
                cropRect: {
                    x: newSize.width * 0.1,
                    y: newSize.height * 0.1,
                    width: newSize.width * 0.8,
                    height: newSize.height * 0.8
                },
                adjustments: state.adjustments,
                rotation: state.rotation,
                selectedFilter: state.selectedFilter
            };

        case 'APPLY_FILTER_PRESET':
            const filter = FILTER_PRESETS[action.payload];
            return {
                ...state,
                adjustments: {
                    brightness: filter.brightness,
                    contrast: filter.contrast,
                    saturation: filter.saturation,
                    sepia: filter.sepia,
                    grayscale: filter.grayscale,
                    blur: filter.blur,
                    hue: filter.hue,
                },
                selectedFilter: action.payload,
            };

        case 'SET_ROTATION':
            const rotationData = typeof action.payload === 'object'
                ? action.payload
                : {
                    rotation: action.payload,
                    imageSize: state.imageSize
                };

            return {
                ...state,
                rotation: rotationData.rotation,
                imageSize: rotationData.imageSize
            };

        case 'SAVE_STATE':
            const currentState = {
                image: state.image,
                imageSize: state.imageSize,
                texts: JSON.parse(JSON.stringify(state.texts)),
                stickers: state.stickers.map(s => ({ ...s, image: s.image })),
                adjustments: { ...state.adjustments },
                rotation: state.rotation,
            };

            const newHistory = state.history.slice(0, state.historyStep + 1);
            newHistory.push(currentState);

            return {
                ...state,
                history: newHistory,
                historyStep: newHistory.length - 1,
            };

        case 'UNDO':
            if (state.historyStep <= 0) return state;
            const prevState = state.history[state.historyStep - 1];
            return {
                ...prevState,
                history: state.history,
                historyStep: state.historyStep - 1,
                cropMode: state.cropMode,
                cropRect: state.cropRect,
                selectedId: state.selectedId,
            };

        case 'REDO':
            if (state.historyStep >= state.history.length - 1) return state;
            const nextState = state.history[state.historyStep + 1];
            return {
                ...nextState,
                history: state.history,
                historyStep: state.historyStep + 1,
                cropMode: state.cropMode,
                cropRect: state.cropRect,
                selectedId: state.selectedId,
            };

        default:
            return state;
    }
}

const KonvaImageEditorModal = ({ visible, imageUrl, onSave, onCancel }) => {
    const { t } = useTranslation();
    const [state, dispatch] = useReducer(editorReducer, initialState);
    const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('social');
    const [zoom, setZoom] = useState(1);
    const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [lastPointerPosition, setLastPointerPosition] = useState({ x: 0, y: 0 });
    const [imageLoading, setImageLoading] = useState(true);
    const [mobileView, setMobileView] = useState('main');
    const [isMobile, setIsMobile] = useState(false);

    const stageRef = useRef(null);
    const imageRef = useRef(null);
    const transformerRef = useRef(null);
    const containerRef = useRef(null);
    const cropTransformerRef = useRef(null);
    const cropRectRef = useRef(null);
    const filtersScrollRef = useRef(null);

    // Определяем мобильное устройство
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (visible && imageUrl) {
            setImageLoading(true);

            const img = new window.Image();
            img.src = imageUrl;
            img.crossOrigin = 'Anonymous';

            img.onload = () => {
                if (img.width > 2000 || img.height > 2000) {
                    const canvas = document.createElement('canvas');
                    const maxSize = 2000;
                    let newWidth, newHeight;

                    if (img.width > img.height) {
                        newWidth = maxSize;
                        newHeight = (img.height * maxSize) / img.width;
                    } else {
                        newHeight = maxSize;
                        newWidth = (img.width * maxSize) / img.height;
                    }

                    canvas.width = newWidth;
                    canvas.height = newHeight;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, newWidth, newHeight);

                    const optimizedImg = new window.Image();
                    optimizedImg.src = canvas.toDataURL('image/jpeg', 0.75);
                    optimizedImg.onload = () => {
                        dispatch({ type: 'LOAD_IMAGE', payload: optimizedImg });
                        setZoom(1);
                        setStagePosition({ x: 0, y: 0 });
                        setImageLoading(false);
                    };
                } else {
                    dispatch({ type: 'LOAD_IMAGE', payload: img });
                    setZoom(1);
                    setStagePosition({ x: 0, y: 0 });
                    setImageLoading(false);
                }
            };

            img.onerror = () => {
                message.error(t('imageEditor.messages.loadError'));
                setImageLoading(false);
            }
        }
    }, [visible, imageUrl, t]);

    useLayoutEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                const containerHeight = containerRef.current.offsetHeight;

                setStageSize({
                    width: containerWidth,
                    height: containerHeight,
                });
            }
        };

        if (visible) {
            updateSize();
            const resizeObserver = new ResizeObserver(updateSize);
            if (containerRef.current) {
                resizeObserver.observe(containerRef.current);
            }
            return () => resizeObserver.disconnect();
        }
    }, [visible]);

    useEffect(() => {
        if (transformerRef.current && state.selectedId) {
            const stage = stageRef.current;
            const selectedNode = stage.findOne('#' + state.selectedId);
            if (selectedNode && selectedNode !== transformerRef.current.nodes()[0]) {
                transformerRef.current.nodes([selectedNode]);
                transformerRef.current.getLayer().batchDraw();
            }
        } else if (transformerRef.current) {
            transformerRef.current.nodes([]);
            transformerRef.current.getLayer().batchDraw();
        }
    }, [state.selectedId]);

    useEffect(() => {
        if (cropTransformerRef.current && state.cropMode && cropRectRef.current) {
            cropTransformerRef.current.nodes([cropRectRef.current]);
            cropTransformerRef.current.getLayer().batchDraw();
        } else if (cropTransformerRef.current) {
            cropTransformerRef.current.nodes([]);
            cropTransformerRef.current.getLayer().batchDraw();
        }
    }, [state.cropMode]);

    useEffect(() => {
        if (imageRef.current && state.image) {
            imageRef.current.cache();
            imageRef.current.getLayer().batchDraw();
        }
    }, [state.image, state.adjustments, state.rotation]);

    const getStageScale = useCallback(() => {
        if (!state.imageSize.width || !state.imageSize.height) return 1;

        const containerWidth = stageSize.width - 40;
        const containerHeight = stageSize.height - 40;

        const scaleX = containerWidth / state.imageSize.width;
        const scaleY = containerHeight / state.imageSize.height;

        return Math.min(scaleX, scaleY, 1) * zoom;
    }, [state.imageSize.width, state.imageSize.height, stageSize, zoom]);

    const scale = getStageScale();

    const stagePos = {
        x: (stageSize.width - state.imageSize.width * scale) / 2,
        y: (stageSize.height - state.imageSize.height * scale) / 2
    };

    const handleWheel = (e) => {
        e.evt.preventDefault();
        const scaleBy = 1.1;
        const oldScale = zoom;

        const pointer = stageRef.current.getPointerPosition();
        const mousePointTo = {
            x: (pointer.x - stagePos.x) / oldScale,
            y: (pointer.y - stagePos.y) / oldScale,
        };

        const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
        const boundedScale = Math.max(0.1, Math.min(2, newScale));

        setZoom(boundedScale);
    };

    const handleStageMouseDown = (e) => {
        if (e.target === e.target.getStage()) {
            setIsDragging(true);
            setLastPointerPosition({ x: e.evt.clientX, y: e.evt.clientY });
        }
        handleCheckDeselect(e);
    };

    const handleStageMouseMove = (e) => {
        if (!isDragging) return;

        const dx = e.evt.clientX - lastPointerPosition.x;
        const dy = e.evt.clientY - lastPointerPosition.y;

        setStagePosition(prev => ({
            x: prev.x + dx,
            y: prev.y + dy,
        }));
        setLastPointerPosition({ x: e.evt.clientX, y: e.evt.clientY });
    };

    const handleStageMouseUp = () => {
        setIsDragging(false);
    };

    const resetZoomAndPosition = () => {
        setZoom(1);
        setStagePosition({ x: 0, y: 0 });
    };

    const fitToScreen = () => {
        if (!state.imageSize.width || !state.imageSize.height) return;
        setZoom(1);
        setStagePosition({ x: 0, y: 0 });
    };

    const handleCheckDeselect = (e) => {
        const clickedOnEmpty = e.target === e.target.getStage();
        if (clickedOnEmpty) {
            dispatch({ type: 'SET_SELECTED', payload: null });
        }
    };

    const saveState = useCallback(() => {
        dispatch({ type: 'SAVE_STATE' });
    }, []);

    const addText = useCallback((textSettings) => {
        const newText = {
            id: 'text-' + Date.now(),
            x: state.imageSize.width / 4,
            y: state.imageSize.height / 4,
            fontSize: 40,
            fontFamily: 'Arial',
            fill: '#ffffff',
            ...textSettings
        };
        dispatch({ type: 'ADD_TEXT', payload: newText });
        setTimeout(saveState, 50);
    }, [state.imageSize, saveState]);

    const addSticker = useCallback((stickerImage) => {
        const newSticker = {
            id: 'sticker-' + Date.now(),
            image: stickerImage,
            x: state.imageSize.width / 3,
            y: state.imageSize.height / 3,
            width: 150,
            height: (150 / stickerImage.width) * stickerImage.height,
        };
        dispatch({ type: 'ADD_STICKER', payload: newSticker });
        setTimeout(saveState, 50);
    }, [state.imageSize, saveState]);

    const applyCropPreset = useCallback((presetKey) => {
        const preset = CROP_PRESETS[presetKey];
        if (!preset.aspect) {
            dispatch({ type: 'TOGGLE_CROP_MODE' });
            return;
        }

        const imageWidth = state.imageSize.width;
        const imageHeight = state.imageSize.height;

        let cropWidth, cropHeight;

        if (imageWidth / imageHeight > preset.aspect) {
            cropHeight = imageHeight * 0.8;
            cropWidth = cropHeight * preset.aspect;
        } else {
            cropWidth = imageWidth * 0.8;
            cropHeight = cropWidth / preset.aspect;
        }

        const x = (imageWidth - cropWidth) / 2;
        const y = (imageHeight - cropHeight) / 2;

        dispatch({
            type: 'SET_CROP_ASPECT',
            payload: {
                aspect: preset.aspect,
                rect: { x, y, width: cropWidth, height: cropHeight }
            }
        });
    }, [state.imageSize]);

    const applySocialPreset = useCallback((presetKey) => {
        const preset = SOCIAL_PRESETS[presetKey];
        const targetAspect = preset.width / preset.height;

        const imageWidth = state.imageSize.width;
        const imageHeight = state.imageSize.height;

        let cropWidth, cropHeight;

        if (imageWidth / imageHeight > targetAspect) {
            cropHeight = imageHeight;
            cropWidth = cropHeight * targetAspect;
        } else {
            cropWidth = imageWidth;
            cropHeight = cropWidth / targetAspect;
        }

        const x = (imageWidth - cropWidth) / 2;
        const y = (imageHeight - cropHeight) / 2;

        dispatch({
            type: 'SET_CROP_ASPECT',
            payload: {
                aspect: targetAspect,
                rect: { x, y, width: cropWidth, height: cropHeight }
            }
        });

        if (isMobile) {
            setMobileView('crop');
        } else {
            setActiveTab('crop');
        }
        message.success(t('imageEditor.messages.socialPresetApplied', { platform: t(preset.nameKey) }));
    }, [state.imageSize, t, isMobile]);

    const handleApplyCrop = useCallback(() => {
        if (!state.cropMode) return;

        const cropRect = state.cropRect;

        const validatedCropRect = {
            x: Math.max(0, Math.min(cropRect.x, state.imageSize.width - 10)),
            y: Math.max(0, Math.min(cropRect.y, state.imageSize.height - 10)),
            width: Math.max(10, Math.min(cropRect.width, state.imageSize.width - cropRect.x)),
            height: Math.max(10, Math.min(cropRect.height, state.imageSize.height - cropRect.y))
        };

        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');

        tempCanvas.width = validatedCropRect.width;
        tempCanvas.height = validatedCropRect.height;

        tempCtx.clearRect(0, 0, validatedCropRect.width, validatedCropRect.height);

        tempCtx.drawImage(
            state.image,
            validatedCropRect.x, validatedCropRect.y, validatedCropRect.width, validatedCropRect.height,
            0, 0, validatedCropRect.width, validatedCropRect.height
        );

        const croppedImage = new window.Image();
        croppedImage.src = tempCanvas.toDataURL('image/png');

        croppedImage.onload = () => {
            const newTexts = state.texts
                .map(text => {
                    const newX = text.x - validatedCropRect.x;
                    const newY = text.y - validatedCropRect.y;

                    const isVisible = (
                        newX + (text.width || text.fontSize * text.text.length * 0.6) > 0 &&
                        newY + (text.height || text.fontSize) > 0 &&
                        newX < validatedCropRect.width &&
                        newY < validatedCropRect.height
                    );

                    return isVisible ? {
                        ...text,
                        x: newX,
                        y: newY
                    } : null;
                })
                .filter(Boolean);

            const newStickers = state.stickers
                .map(sticker => {
                    const newX = sticker.x - validatedCropRect.x;
                    const newY = sticker.y - validatedCropRect.y;

                    const isVisible = (
                        newX + sticker.width > 0 &&
                        newY + sticker.height > 0 &&
                        newX < validatedCropRect.width &&
                        newY < validatedCropRect.height
                    );

                    return isVisible ? {
                        ...sticker,
                        x: newX,
                        y: newY
                    } : null;
                })
                .filter(Boolean);

            dispatch({
                type: 'APPLY_CROP',
                payload: {
                    newSize: {
                        width: validatedCropRect.width,
                        height: validatedCropRect.height
                    },
                    newTexts,
                    newStickers,
                    newImage: croppedImage
                }
            });

            saveState();
            message.success(t('imageEditor.messages.cropSuccess'));
            resetZoomAndPosition();
        };

        croppedImage.onerror = () => {
            message.error(t('imageEditor.messages.cropError'));
        };
    }, [state.cropMode, state.cropRect, state.image, state.texts, state.stickers, state.imageSize, saveState, t]);

    const rotateImage = useCallback((angle) => {
        const newRotation = (state.rotation + angle) % 360;
        dispatch({ type: 'SET_ROTATION', payload: newRotation });
        setTimeout(saveState, 50);
    }, [state.rotation, saveState]);

    const applyFilter = useCallback((filterKey) => {
        //ЕБЕЙШИЙ ПОДХОД: КТО ПРОЧИТАЛ, ТОТ ЛОХ 
        //ДА, ТУПОЙ СМУЗИХЛЕБ ЕСТЬ УТЕЧКИ ПАМЯТИ
        //НО РАЗ РЕШИЛ ДОЕБАТЬ, ТО ИСПРАВЬ
        const debounce = (func, wait) => {
            let timeout;
            return (...args) => {
                clearTimeout(timeout);
                timeout = setTimeout(() => func(...args), wait);
            };
        };

        const debouncedDispatch = debounce((key) => {
            dispatch({ type: 'APPLY_FILTER_PRESET', payload: key });
            setTimeout(saveState, 50);
        }, 300);

        debouncedDispatch(filterKey);
    }, [saveState]);

    const resetFilters = useCallback(() => {
        dispatch({ type: 'APPLY_FILTER_PRESET', payload: 'none' });
        setTimeout(saveState, 50);
    }, [saveState]);

    const handleCropTransform = useCallback(() => {
        if (!state.cropMode || !cropTransformerRef.current) return;

        const node = cropRectRef.current;
        const transformer = cropTransformerRef.current;

        if (node && transformer) {
            node.scaleX(1);
            node.scaleY(1);

            let newRect = {
                x: Math.max(0, node.x()),
                y: Math.max(0, node.y()),
                width: Math.max(50, node.width()),
                height: Math.max(50, node.height()),
            };

            if (newRect.x + newRect.width > state.imageSize.width) {
                newRect.width = state.imageSize.width - newRect.x;
            }
            if (newRect.y + newRect.height > state.imageSize.height) {
                newRect.height = state.imageSize.height - newRect.y;
            }

            if (state.cropAspect) {
                const currentAspect = newRect.width / newRect.height;
                const aspectDiff = Math.abs(currentAspect - state.cropAspect);

                if (aspectDiff > 0.001) {
                    if (currentAspect > state.cropAspect) {
                        newRect.width = newRect.height * state.cropAspect;
                    } else {
                        newRect.height = newRect.width / state.cropAspect;
                    }

                    if (newRect.x + newRect.width > state.imageSize.width) {
                        newRect.x = state.imageSize.width - newRect.width;
                    }
                    if (newRect.y + newRect.height > state.imageSize.height) {
                        newRect.y = state.imageSize.height - newRect.height;
                    }
                }
            }

            newRect.x = Math.max(0, Math.min(newRect.x, state.imageSize.width - 10));
            newRect.y = Math.max(0, Math.min(newRect.y, state.imageSize.height - 10));
            newRect.width = Math.max(10, Math.min(newRect.width, state.imageSize.width - newRect.x));
            newRect.height = Math.max(10, Math.min(newRect.height, state.imageSize.height - newRect.y));

            dispatch({ type: 'UPDATE_CROP_RECT', payload: newRect });

            node.x(newRect.x);
            node.y(newRect.y);
            node.width(newRect.width);
            node.height(newRect.height);

            transformer.forceUpdate();
        }
    }, [state.cropMode, state.cropAspect, state.imageSize]);

    const handleSave = async () => {
        setLoading(true);
        dispatch({ type: 'SET_SELECTED', payload: null });

        setTimeout(async () => {
            try {
                const tempCanvas = document.createElement('canvas');
                const tempCtx = tempCanvas.getContext('2d');

                if (state.rotation === 90 || state.rotation === 270) {
                    tempCanvas.width = state.imageSize.height;
                    tempCanvas.height = state.imageSize.width;
                } else {
                    tempCanvas.width = state.imageSize.width;
                    tempCanvas.height = state.imageSize.height;
                }

                tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);

                const filterString = `
                brightness(${(100 + state.adjustments.brightness) / 100})
                contrast(${(100 + state.adjustments.contrast) / 100})
                saturate(${(100 + state.adjustments.saturation) / 100})
                sepia(${state.adjustments.sepia}%)
                grayscale(${state.adjustments.grayscale}%)
                blur(${state.adjustments.blur}px)
                hue-rotate(${state.adjustments.hue}deg)
            `;

                tempCtx.filter = filterString;

                tempCtx.save();

                tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);

                tempCtx.rotate((state.rotation * Math.PI) / 180);

                if (state.rotation === 90 || state.rotation === 270) {
                    tempCtx.drawImage(
                        state.image,
                        -tempCanvas.height / 2,
                        -tempCanvas.width / 2,
                        tempCanvas.height,
                        tempCanvas.width
                    );
                } else {
                    tempCtx.drawImage(
                        state.image,
                        -tempCanvas.width / 2,
                        -tempCanvas.height / 2,
                        tempCanvas.width,
                        tempCanvas.height
                    );
                }

                tempCtx.restore();

                state.texts.forEach(text => {
                    tempCtx.save();

                    const fontStyle = text.fontStyle || 'normal';
                    const fontWeight = fontStyle.includes('bold') ? 'bold' : 'normal';
                    const fontStyleValue = fontStyle.includes('italic') ? 'italic' : 'normal';

                    tempCtx.font = `${fontStyleValue} ${fontWeight} ${text.fontSize}px ${text.fontFamily}`;
                    tempCtx.fillStyle = text.fill;
                    tempCtx.textAlign = 'left';
                    tempCtx.textBaseline = 'alphabetic';

                    const textMetrics = tempCtx.measureText(text.text);
                    const textWidth = textMetrics.width;
                    const textHeight = text.fontSize;

                    if (text.rotation) {
                        tempCtx.translate(text.x + textWidth / 2, text.y + textHeight / 2);
                        tempCtx.rotate((text.rotation * Math.PI) / 180);
                        tempCtx.fillText(text.text, -textWidth / 2, -textHeight / 2);

                        if (text.textDecoration === 'underline') {
                            tempCtx.strokeStyle = text.fill;
                            tempCtx.lineWidth = Math.max(1, text.fontSize * 0.08);
                            tempCtx.beginPath();
                            const underlineY = textHeight / 2 + text.fontSize * 0.1;
                            tempCtx.moveTo(-textWidth / 2, underlineY);
                            tempCtx.lineTo(textWidth / 2, underlineY);
                            tempCtx.stroke();
                        }
                    } else {
                        tempCtx.fillText(text.text, text.x, text.y + textHeight);

                        if (text.textDecoration === 'underline') {
                            tempCtx.strokeStyle = text.fill;
                            tempCtx.lineWidth = Math.max(1, text.fontSize * 0.08);
                            tempCtx.beginPath();
                            const underlineY = text.y + textHeight + text.fontSize * 0.1;
                            tempCtx.moveTo(text.x, underlineY);
                            tempCtx.lineTo(text.x + textWidth, underlineY);
                            tempCtx.stroke();
                        }
                    }

                    tempCtx.restore();
                });

                state.stickers.forEach(sticker => {
                    const isVisible =
                        sticker.x + sticker.width > 0 &&
                        sticker.y + sticker.height > 0 &&
                        sticker.x < state.imageSize.width &&
                        sticker.y < state.imageSize.height;

                    if (!isVisible) return;

                    tempCtx.save();

                    if (sticker.rotation) {
                        tempCtx.translate(sticker.x + sticker.width / 2, sticker.y + sticker.height / 2);
                        tempCtx.rotate((sticker.rotation * Math.PI) / 180);
                        tempCtx.drawImage(
                            sticker.image,
                            -sticker.width / 2,
                            -sticker.height / 2,
                            sticker.width,
                            sticker.height
                        );
                    } else {
                        tempCtx.drawImage(
                            sticker.image,
                            sticker.x,
                            sticker.y,
                            sticker.width,
                            sticker.height
                        );
                    }

                    tempCtx.restore();
                });

                const useJPEG = state.imageSize.width > 800 || state.imageSize.height > 800;

                const dataURL = useJPEG
                    ? tempCanvas.toDataURL('image/jpeg', 0.85)
                    : tempCanvas.toDataURL('image/png', 0.9);


                const res = await fetch(dataURL);
                const blob = await res.blob();
                const file = new File([blob], `edited-image-${Date.now()}.jpeg`, { type: 'image/jpeg' });
                console.log(blob.size)
                await onSave(file);

            } catch (error) {
                console.error('Save error:', error);
                message.error(t('imageEditor.messages.saveError'));
            } finally {
                setLoading(false);
            }
        }, 100);
    };

    const rotateEntireImage = useCallback((angle) => {
        const newRotation = (state.rotation + angle) % 360;

        let newWidth = state.imageSize.width;
        let newHeight = state.imageSize.height;

        if (angle === 90 || angle === 270) {
            newWidth = state.imageSize.height;
            newHeight = state.imageSize.width;
        }

        dispatch({
            type: 'SET_ROTATION',
            payload: {
                rotation: newRotation,
                imageSize: {
                    width: newWidth,
                    height: newHeight
                }
            }
        });

        setTimeout(saveState, 50);
    }, [state.rotation, state.imageSize, saveState]);



    const MobileFiltersFullView = () => {
        return (
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                padding: '16px',
                height: '200px',
                overflowY: 'auto'
            }}>
                {Object.entries(FILTER_PRESETS).map(([key, filter]) => (
                    <div
                        key={key}
                        onClick={() => {
                            applyFilter(key);
                        }}
                        style={{
                            padding: '16px 8px',
                            border: state.selectedFilter === key ? '2px solid #0095f6' : '1px solid #444',
                            borderRadius: '12px',
                            textAlign: 'center',
                            backgroundColor: state.selectedFilter === key ? '#0095f615' : '#2a2a2a',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            minHeight: '60px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <span style={{
                            color: state.selectedFilter === key ? '#0095f6' : 'white',
                            fontSize: '13px',
                            fontWeight: state.selectedFilter === key ? '600' : '400',
                            lineHeight: '1.2'
                        }}>
                            {t(filter.nameKey)}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    const MobileToolsView = () => {
        const tools = [
            {
                key: 'adjust',
                icon: <BgColorsOutlined />,
                label: t('imageEditor.adjust.title'),
                color: '#ff6b6b'
            },
            {
                key: 'crop',
                icon: <ScissorOutlined />,
                label: t('imageEditor.crop.title'),
                color: '#4ecdc4'
            },
            {
                key: 'text',
                icon: <FontSizeOutlined />,
                label: t('imageEditor.text.title'),
                color: '#45b7d1'
            },
            {
                key: 'stickers',
                icon: <PictureOutlined />,
                label: t('imageEditor.stickers.title'),
                color: '#96ceb4'
            }
        ];

        return (
            <div style={{
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 10px',
                backgroundColor: '#1a1a1a',
                borderTop: '1px solid #333'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    width: '100%'
                }}>
                    {tools.map(tool => (
                        <div
                            key={tool.key}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                cursor: 'pointer'
                            }}
                            onClick={() => setMobileView(tool.key)}
                        >
                            <div
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    backgroundColor: tool.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '18px',
                                    marginBottom: '5px'
                                }}
                            >
                                {tool.icon}
                            </div>
                            <span style={{ color: 'white', fontSize: '10px' }}>
                                {tool.label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const MobileAdjustView = () => {
        const handleAdjustmentChange = (key, value) => {
            dispatch({ type: 'SET_ADJUSTMENT', payload: { [key]: value } });
        };

        return (
            <div style={{
                height: '200px',
                backgroundColor: '#1a1a1a',
                padding: '15px',
                overflowY: 'auto'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '15px',
                    color: 'white'
                }}>
                    <Button
                        type="text"
                        icon={<LeftOutlined />}
                        style={{ color: 'white' }}
                        onClick={() => setMobileView('main')}
                    />
                    <h4 style={{ margin: '0 15px', color: 'white' }}>
                        {t('imageEditor.adjust.title')}
                    </h4>
                </div>

                {Object.entries(state.adjustments).map(([key, value]) => (
                    <div key={key} style={{ marginBottom: '15px' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '5px',
                            color: 'white'
                        }}>
                            <span style={{ textTransform: 'capitalize', fontSize: '12px' }}>
                                {t(`imageEditor.adjust.${key}`)}
                            </span>
                            <span style={{ fontSize: '12px', color: '#0095f6' }}>
                                {value}
                            </span>
                        </div>
                        <Slider
                            min={key === 'contrast' ? -100 : 0}
                            max={key === 'hue' ? 360 : (key === 'contrast' ? 100 : 200)}
                            value={value}
                            onChange={(val) => handleAdjustmentChange(key, val)}
                            trackStyle={{ backgroundColor: '#0095f6' }}
                            handleStyle={{ borderColor: '#0095f6' }}
                        />
                    </div>
                ))}

                <Button
                    onClick={resetFilters}
                    style={{
                        width: '100%',
                        backgroundColor: 'transparent',
                        border: '1px solid #666',
                        color: 'white',
                        marginTop: '10px'
                    }}
                >
                    {t('imageEditor.adjust.reset')}
                </Button>
            </div>
        );
    };

    const MobileTextView = () => {
        const { t } = useTranslation();
        const [textValue, setTextValue] = useState(t('imageEditor.text.placeholder'));
        const [textColor, setTextColor] = useState('#ffffff');
        const [fontSize, setFontSize] = useState(32);

        return (
            <div style={{
                height: '300px',
                backgroundColor: '#1a1a1a',
                padding: '15px'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '15px',
                    color: 'white'
                }}>
                    <Button
                        type="text"
                        icon={<LeftOutlined />}
                        style={{ color: 'white' }}
                        onClick={() => setMobileView('main')}
                    />
                    <h4 style={{ margin: '0 15px', color: 'white' }}>
                        {t('imageEditor.text.title')}
                    </h4>
                </div>

                <Input.TextArea
                    value={textValue}
                    onChange={e => setTextValue(e.target.value)}
                    placeholder={t('imageEditor.text.placeholder')}
                    rows={2}
                    style={{
                        backgroundColor: '#333',
                        border: '1px solid #555',
                        color: 'white',
                        marginBottom: '10px'
                    }}
                />

                <div style={{ marginBottom: '10px' }}>
                    <div style={{ color: 'white', fontSize: '12px', marginBottom: '5px' }}>
                        {t('imageEditor.text.size')}: {fontSize}
                    </div>
                    <Slider
                        min={16}
                        max={72}
                        value={fontSize}
                        onChange={setFontSize}
                        trackStyle={{ backgroundColor: '#0095f6' }}
                        handleStyle={{ borderColor: '#0095f6' }}
                    />
                </div>

                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', alignItems: 'center' }}>
                    <ColorPicker
                        value={textColor}
                        onChange={c => setTextColor(c.toHexString())}
                        showText
                        size="small"
                    />
                    <span style={{ fontSize: '12px', color: '#ccc' }}>{textColor}</span>
                </div>

                <Button
                    type="primary"
                    block
                    onClick={() => {
                        addText({
                            text: textValue,
                            fill: textColor,
                            fontSize,
                            fontFamily: 'Arial'
                        });
                        setMobileView('main');
                    }}
                    style={{
                        backgroundColor: '#0095f6',
                        borderColor: '#0095f6',
                        height: '40px'
                    }}
                >
                    {t('imageEditor.text.add')}
                </Button>
            </div>
        );
    };

    const MobileCropView = () => {
        return (
            <div style={{
                height: '200px',
                backgroundColor: '#1a1a1a',
                padding: '15px',
                color: 'white'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '15px'
                }}>
                    <Button
                        type="text"
                        icon={<LeftOutlined />}
                        style={{ color: 'white' }}
                        onClick={() => setMobileView('main')}
                    />
                    <h4 style={{ margin: '0 15px' }}>
                        {t('imageEditor.crop.title')}
                    </h4>
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <div style={{ fontSize: '12px', marginBottom: '10px', color: '#ccc' }}>
                        {t('imageEditor.crop.presets')}
                    </div>
                    <div style={{ display: 'flex', overflowX: 'auto', gap: '10px', paddingBottom: '5px' }}>
                        {Object.entries(CROP_PRESETS).map(([key, preset]) => (
                            <div
                                key={key}
                                style={{
                                    padding: '8px 12px',
                                    backgroundColor: '#333',
                                    borderRadius: '20px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    border: state.cropMode && state.cropAspect === preset.aspect ? '1px solid #0095f6' : '1px solid #555'
                                }}
                                onClick={() => applyCropPreset(key)}
                            >
                                {t(preset.nameKey)}
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                    <Button
                        type={state.cropMode ? 'primary' : 'default'}
                        onClick={() => dispatch({ type: 'TOGGLE_CROP_MODE' })}
                        style={{ flex: 1 }}
                    >
                        {state.cropMode ? t('imageEditor.crop.exit') : t('imageEditor.crop.enter')}
                    </Button>
                    {state.cropMode && (
                        <Button
                            type="primary"
                            onClick={handleApplyCrop}
                            style={{ flex: 1 }}
                        >
                            {t('imageEditor.crop.apply')}
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    const MobileStickersView = () => {
        const { t } = useTranslation();

        const handleStickerUpload = (file) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new window.Image();
                img.src = e.target.result;
                img.onload = () => {
                    addSticker(img);
                    message.success(t('imageEditor.messages.stickerAdded'));
                };
            };
            reader.readAsDataURL(file);
            return false;
        };

        return (
            <div style={{
                height: '300px',
                backgroundColor: '#1a1a1a',
                padding: '15px',
                color: 'white'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '15px'
                }}>
                    <Button
                        type="text"
                        icon={<LeftOutlined />}
                        style={{ color: 'white' }}
                        onClick={() => setMobileView('main')}
                    />
                    <h4 style={{ margin: '0 15px' }}>
                        {t('imageEditor.stickers.title')}
                    </h4>
                </div>

                <Upload
                    beforeUpload={handleStickerUpload}
                    showUploadList={false}
                    accept="image/*"
                >
                    <Button
                        block
                        icon={<PictureOutlined />}
                        style={{
                            backgroundColor: '#0095f6',
                            borderColor: '#0095f6',
                            color: 'white',
                            marginBottom: '15px',
                            height: '40px'
                        }}
                    >
                        {t('imageEditor.stickers.upload')}
                    </Button>
                </Upload>

                <div style={{
                    backgroundColor: '#333',
                    padding: '12px',
                    borderRadius: '8px',
                    fontSize: '12px'
                }}>
                    <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                        Инструкция:
                    </div>
                    <div style={{ color: '#ccc', lineHeight: '1.4' }}>
                        • Загрузите стикер<br />
                        • Тапайте для выбора<br />
                        • Двигайте пальцем<br />
                        • Удаляйте через кнопку удаления
                    </div>
                </div>
            </div>
        );
    };

    if (!isMobile) {
        return (
            <Modal
                open={visible}
                title={t('imageEditor.title')}
                onCancel={onCancel}
                width="95vw"
                style={{ maxWidth: 1800, top: 20 }}
                footer={
                    <Space>
                        <Button
                            icon={<UndoOutlined />}
                            onClick={() => dispatch({ type: 'UNDO' })}
                            disabled={state.historyStep <= 0}
                        >
                            {t('imageEditor.undo')}
                        </Button>
                        <Button
                            icon={<RedoOutlined />}
                            onClick={() => dispatch({ type: 'REDO' })}
                            disabled={state.historyStep >= state.history.length - 1}
                        >
                            {t('imageEditor.redo')}
                        </Button>
                        <Button
                            icon={<DeleteOutlined />}
                            onClick={() => dispatch({ type: 'DELETE_SELECTED' })}
                            danger
                            disabled={!state.selectedId}
                        >
                            {t('imageEditor.delete')}
                        </Button>
                        <Button onClick={onCancel}>{t('imageEditor.cancel')}</Button>
                        <Button type="primary" onClick={handleSave} loading={loading}>
                            {t('imageEditor.save')}
                        </Button>
                    </Space>
                }
            >
                <div className='flex w-full' style={{ gap: '16px' }}>
                    <div
                        ref={containerRef}
                        style={{
                            width: '70%',
                            height: '75vh',
                            backgroundColor: '#f0f0f0',
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: isDragging ? 'grabbing' : 'grab',
                            borderRadius: '8px',
                            border: '1px solid #d9d9d9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        {imageLoading && (
                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
                                <Spin size="large" />
                            </div>
                        )}

                        <div style={{
                            position: 'absolute',
                            top: 10,
                            left: 10,
                            zIndex: 10,
                            background: 'rgba(255,255,255,0.95)',
                            padding: '8px',
                            borderRadius: '8px',
                            display: 'flex',
                            gap: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            border: '1px solid #d9d9d9'
                        }}>
                            <Tooltip title={t('imageEditor.rotateLeft')}>
                                <Button
                                    icon={<RotateLeftOutlined />}
                                    onClick={() => rotateImage(-90)}
                                    size="small"
                                    type="text"
                                />
                            </Tooltip>
                            <Tooltip title={t('imageEditor.rotateRight')}>
                                <Button
                                    icon={<RotateRightOutlined />}
                                    onClick={() => rotateImage(90)}
                                    size="small"
                                    type="text"
                                />
                            </Tooltip>
                            <Tooltip title={t('imageEditor.crop.enter')}>
                                <Button
                                    icon={<ScissorOutlined />}
                                    type={state.cropMode ? "primary" : "text"}
                                    onClick={() => dispatch({ type: 'TOGGLE_CROP_MODE' })}
                                    size="small"
                                />
                            </Tooltip>
                            <Divider type="vertical" />
                            <Tooltip title={t('imageEditor.zoom.out')}>
                                <Button
                                    icon={<ZoomOutOutlined />}
                                    onClick={() => setZoom(prev => Math.max(0.1, prev - 0.1))}
                                    size="small"
                                    type="text"
                                    disabled={zoom <= 0.1}
                                />
                            </Tooltip>
                            <Tooltip title={`${Math.round(zoom * 100)}%`}>
                                <Button
                                    size="small"
                                    type="text"
                                    style={{ minWidth: 60, fontSize: 12 }}
                                    onClick={resetZoomAndPosition}
                                >
                                    {Math.round(zoom * 100)}%
                                </Button>
                            </Tooltip>
                            <Tooltip title={t('imageEditor.zoom.in')}>
                                <Button
                                    icon={<ZoomInOutlined />}
                                    onClick={() => setZoom(prev => Math.min(2, prev + 0.1))}
                                    size="small"
                                    type="text"
                                    disabled={zoom >= 2}
                                />
                            </Tooltip>
                            <Tooltip title={t('imageEditor.zoom.fit')}>
                                <Button
                                    icon={<ExpandOutlined />}
                                    onClick={fitToScreen}
                                    size="small"
                                    type="text"
                                />
                            </Tooltip>
                        </div>

                        <div style={{
                            position: 'absolute',
                            bottom: 10,
                            left: 10,
                            zIndex: 10,
                            background: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontFamily: 'monospace'
                        }}>
                            {state.imageSize.width} × {state.imageSize.height}
                            {state.cropMode && (
                                <span style={{ color: '#52c41a', marginLeft: 8 }}>
                                    Crop: {Math.round(state.cropRect.width)} × {Math.round(state.cropRect.height)}
                                </span>
                            )}
                        </div>

                        {state.image && (
                            <Stage
                                width={stageSize.width}
                                height={stageSize.height}
                                ref={stageRef}
                                scaleX={scale}
                                scaleY={scale}
                                x={stagePos.x + stagePosition.x}
                                y={stagePos.y + stagePosition.y}
                                onMouseDown={handleStageMouseDown}
                                onMousemove={handleStageMouseMove}
                                onMouseup={handleStageMouseUp}
                                onMouseleave={handleStageMouseUp}
                                onTouchStart={handleStageMouseDown}
                                onTouchMove={handleStageMouseMove}
                                onTouchEnd={handleStageMouseUp}
                                onWheel={handleWheel}
                                draggable={isDragging}
                            >
                                <Layer>
                                    <Rect
                                        x={0}
                                        y={0}
                                        width={state.imageSize.width}
                                        height={state.imageSize.height}
                                        stroke="#ddd"
                                        strokeWidth={1}
                                        dash={[2, 2]}
                                    />

                                    <Image
                                        ref={imageRef}
                                        image={state.image}
                                        width={state.imageSize.width}
                                        height={state.imageSize.height}
                                        x={state.imageSize.width / 2}
                                        y={state.imageSize.height / 2}
                                        offsetX={state.imageSize.width / 2}
                                        offsetY={state.imageSize.height / 2}
                                        rotation={state.rotation}
                                        filters={[
                                            Konva.Filters.Brighten,
                                            Konva.Filters.Contrast,
                                            Konva.Filters.HSL,
                                            Konva.Filters.Saturate,
                                            Konva.Filters.Sepia,
                                            Konva.Filters.Grayscale,
                                            Konva.Filters.Blur
                                        ]}
                                        brightness={state.adjustments.brightness / 100}
                                        contrast={state.adjustments.contrast / 100}
                                        hue={state.adjustments.hue}
                                        saturation={state.adjustments.saturation / 100}
                                        luminance={0}
                                        sepia={state.adjustments.sepia / 100}
                                        grayscale={state.adjustments.grayscale / 100}
                                        blurRadius={state.adjustments.blur}
                                        cached={true}
                                    />

                                    {state.texts.map(text => (
                                        <Text
                                            key={text.id}
                                            id={text.id}
                                            {...text}
                                            draggable
                                            onClick={() => dispatch({ type: 'SET_SELECTED', payload: text.id })}
                                            onTap={() => dispatch({ type: 'SET_SELECTED', payload: text.id })}
                                            onDragEnd={(e) => {
                                                dispatch({
                                                    type: 'UPDATE_OBJECT_PROPS',
                                                    payload: {
                                                        id: text.id,
                                                        props: {
                                                            x: e.target.x(),
                                                            y: e.target.y()
                                                        }
                                                    }
                                                });
                                            }}
                                            onTransformEnd={(e) => {
                                                const node = e.target;
                                                const scaleX = node.scaleX();
                                                dispatch({
                                                    type: 'UPDATE_OBJECT_PROPS',
                                                    payload: {
                                                        id: text.id,
                                                        props: {
                                                            x: node.x(),
                                                            y: node.y(),
                                                            rotation: node.rotation(),
                                                            fontSize: Math.max(5, text.fontSize * scaleX)
                                                        }
                                                    }
                                                });
                                                node.scaleX(1);
                                                node.scaleY(1);
                                            }}
                                        />
                                    ))}

                                    {state.stickers.map(sticker => (
                                        <Image
                                            key={sticker.id}
                                            id={sticker.id}
                                            image={sticker.image}
                                            x={sticker.x}
                                            y={sticker.y}
                                            width={sticker.width}
                                            height={sticker.height}
                                            rotation={sticker.rotation || 0}
                                            draggable
                                            onClick={() => dispatch({ type: 'SET_SELECTED', payload: sticker.id })}
                                            onTap={() => dispatch({ type: 'SET_SELECTED', payload: sticker.id })}
                                            onDragEnd={(e) => {
                                                dispatch({
                                                    type: 'UPDATE_OBJECT_PROPS',
                                                    payload: {
                                                        id: sticker.id,
                                                        props: {
                                                            x: e.target.x(),
                                                            y: e.target.y()
                                                        }
                                                    }
                                                });
                                            }}
                                            onTransformEnd={(e) => {
                                                const node = e.target;
                                                const scaleX = node.scaleX();
                                                dispatch({
                                                    type: 'UPDATE_OBJECT_PROPS',
                                                    payload: {
                                                        id: sticker.id,
                                                        props: {
                                                            x: node.x(),
                                                            y: node.y(),
                                                            rotation: node.rotation(),
                                                            width: Math.max(20, sticker.width * scaleX),
                                                            height: Math.max(20, sticker.height * scaleX)
                                                        }
                                                    }
                                                });
                                                node.scaleX(1);
                                                node.scaleY(1);
                                            }}
                                        />
                                    ))}

                                    <Transformer
                                        ref={transformerRef}
                                    />

                                    {state.cropMode && (
                                        <>
                                            <Rect
                                                ref={cropRectRef}
                                                x={state.cropRect.x}
                                                y={state.cropRect.y}
                                                width={state.cropRect.width}
                                                height={state.cropRect.height}
                                                fill="rgba(0,0,0,0.3)"
                                                stroke="#1890ff"
                                                strokeWidth={2}
                                                dash={[5, 5]}
                                                draggable
                                                onDragEnd={(e) => {
                                                    const node = e.target;
                                                    const newRect = {
                                                        x: node.x(),
                                                        y: node.y(),
                                                        width: state.cropRect.width,
                                                        height: state.cropRect.height
                                                    };
                                                    dispatch({ type: 'UPDATE_CROP_RECT', payload: newRect });
                                                }}
                                            />
                                            <Transformer
                                                ref={cropTransformerRef}
                                                boundBoxFunc={(oldBox, newBox) => {
                                                    const minSize = 20;

                                                    if (newBox.width < minSize || newBox.height < minSize) {
                                                        return oldBox;
                                                    }

                                                    newBox.x = Math.max(0, newBox.x);
                                                    newBox.y = Math.max(0, newBox.y);
                                                    newBox.width = Math.min(newBox.width, state.imageSize.width - newBox.x);
                                                    newBox.height = Math.min(newBox.height, state.imageSize.height - newBox.y);

                                                    if (state.cropAspect) {
                                                        const currentAspect = newBox.width / newBox.height;
                                                        if (Math.abs(currentAspect - state.cropAspect) > 0.01) {
                                                            const centerX = newBox.x + newBox.width / 2;
                                                            const centerY = newBox.y + newBox.height / 2;

                                                            if (currentAspect > state.cropAspect) {
                                                                newBox.width = newBox.height * state.cropAspect;
                                                            } else {
                                                                newBox.height = newBox.width / state.cropAspect;
                                                            }

                                                            newBox.x = centerX - newBox.width / 2;
                                                            newBox.y = centerY - newBox.height / 2;
                                                        }
                                                    }

                                                    return newBox;
                                                }}
                                                onTransformEnd={handleCropTransform}
                                            />
                                        </>
                                    )}
                                </Layer>
                            </Stage>
                        )}
                    </div>

                    <ControlsPanel
                        state={state}
                        dispatch={dispatch}
                        onAddText={addText}
                        onAddSticker={addSticker}
                        onApplyCrop={handleApplyCrop}
                        onApplyFilter={applyFilter}
                        onResetFilters={resetFilters}
                        onApplyCropPreset={applyCropPreset}
                        onApplySocialPreset={applySocialPreset}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                    />
                </div>
            </Modal>
        );
    }

    return (
        <>
            <Modal
                open={visible}
                title={t('imageEditor.title')}
                onCancel={onCancel}
                width="100vw"
                style={{
                    top: 0,
                    margin: 0,
                    padding: 0,
                    maxWidth: '100vw'
                }}
                bodyStyle={{
                    padding: '10px',
                    height: '100vh',
                    backgroundColor: '#000'
                }}
                footer={null}
                closable={false}
            >
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px',
                    backgroundColor: '#000',
                    borderBottom: '1px solid #333'
                }}>
                    <Button
                        onClick={onCancel}
                        type="text"
                        style={{ color: 'white' }}
                    >
                        {t('imageEditor.cancel')}
                    </Button>

                    <Space>
                        <Tooltip title={t('imageEditor.rotateLeft')}>
                            <Button
                                icon={<RotateLeftOutlined />}
                                onClick={() => rotateImage(-90)}
                                type="text"
                                style={{ color: 'white' }}
                            />
                        </Tooltip>
                        <Tooltip title={t('imageEditor.rotateRight')}>
                            <Button
                                icon={<RotateRightOutlined />}
                                onClick={() => rotateImage(90)}
                                type="text"
                                style={{ color: 'white' }}
                            />
                        </Tooltip>
                    </Space>

                    <Button
                        type="primary"
                        onClick={handleSave}
                        loading={loading}
                        style={{ backgroundColor: '#0095f6', borderColor: '#0095f6' }}
                    >
                        {t('imageEditor.save')}
                    </Button>
                </div>

                <div
                    ref={containerRef}
                    style={{
                        width: '100%',
                        height: 'calc(100vh - 400px)',
                        backgroundColor: '#000',
                        position: 'relative',
                        overflow: 'hidden',
                        cursor: isDragging ? 'grabbing' : 'grab',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {imageLoading && (
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
                            <Spin size="large" />
                        </div>
                    )}

                    {state.image && (
                        <Stage
                            width={stageSize.width}
                            height={stageSize.height}
                            ref={stageRef}
                            scaleX={scale}
                            scaleY={scale}
                            x={stagePos.x + stagePosition.x}
                            y={stagePos.y + stagePosition.y}
                            onMouseDown={handleStageMouseDown}
                            onMousemove={handleStageMouseMove}
                            onMouseup={handleStageMouseUp}
                            onMouseleave={handleStageMouseUp}
                            onTouchStart={handleStageMouseDown}
                            onTouchMove={handleStageMouseMove}
                            onTouchEnd={handleStageMouseUp}
                            onWheel={handleWheel}
                            draggable={isDragging}
                        >
                            <Layer>
                                <Image
                                    ref={imageRef}
                                    image={state.image}
                                    width={state.imageSize.width}
                                    height={state.imageSize.height}
                                    filters={[
                                        Konva.Filters.Brighten,
                                        Konva.Filters.Contrast,
                                        Konva.Filters.HSL,
                                        Konva.Filters.Saturate,
                                        Konva.Filters.Sepia,
                                        Konva.Filters.Grayscale,
                                        Konva.Filters.Blur
                                    ]}
                                    brightness={state.adjustments.brightness / 100}
                                    contrast={state.adjustments.contrast / 100}
                                    hue={state.adjustments.hue}
                                    saturation={state.adjustments.saturation / 100}
                                    luminance={0}
                                    sepia={state.adjustments.sepia / 100}
                                    grayscale={state.adjustments.grayscale / 100}
                                    blurRadius={state.adjustments.blur}
                                    rotation={state.rotation}
                                    cached={true}
                                />

                                {state.texts.map(text => (
                                    <Text
                                        key={text.id}
                                        id={text.id}
                                        {...text}
                                        className="mobile-text-optimized"
                                        fontSize={text.fontSize * 1.5} // Увеличиваем базовый размер шрифта
                                        draggable
                                        onClick={() => dispatch({ type: 'SET_SELECTED', payload: text.id })}
                                        onTap={() => dispatch({ type: 'SET_SELECTED', payload: text.id })}
                                        onDragEnd={(e) => {
                                            dispatch({
                                                type: 'UPDATE_OBJECT_PROPS',
                                                payload: {
                                                    id: text.id,
                                                    props: {
                                                        x: e.target.x(),
                                                        y: e.target.y()
                                                    }
                                                }
                                            });
                                        }}
                                        onTransformEnd={(e) => {
                                            const node = e.target;
                                            const scaleX = node.scaleX();
                                            dispatch({
                                                type: 'UPDATE_OBJECT_PROPS',
                                                payload: {
                                                    id: text.id,
                                                    props: {
                                                        x: node.x(),
                                                        y: node.y(),
                                                        rotation: node.rotation(),
                                                        fontSize: Math.max(16, text.fontSize * scaleX) // Минимальный размер увеличен
                                                    }
                                                }
                                            });
                                            node.scaleX(1);
                                            node.scaleY(1);
                                        }}
                                    />
                                ))}

                                {state.stickers.map(sticker => (
                                    <Image
                                        key={sticker.id}
                                        id={sticker.id}
                                        image={sticker.image}
                                        x={sticker.x}
                                        y={sticker.y}
                                        width={sticker.width * 1.3} // Увеличиваем базовый размер
                                        height={sticker.height * 1.3}
                                        className="mobile-sticker-optimized"
                                        rotation={sticker.rotation || 0}
                                        draggable
                                        onClick={() => dispatch({ type: 'SET_SELECTED', payload: sticker.id })}
                                        onTap={() => dispatch({ type: 'SET_SELECTED', payload: sticker.id })}
                                        onDragEnd={(e) => {
                                            dispatch({
                                                type: 'UPDATE_OBJECT_PROPS',
                                                payload: {
                                                    id: sticker.id,
                                                    props: {
                                                        x: e.target.x(),
                                                        y: e.target.y()
                                                    }
                                                }
                                            });
                                        }}
                                        onTransformEnd={(e) => {
                                            const node = e.target;
                                            const scaleX = node.scaleX();
                                            dispatch({
                                                type: 'UPDATE_OBJECT_PROPS',
                                                payload: {
                                                    id: sticker.id,
                                                    props: {
                                                        x: node.x(),
                                                        y: node.y(),
                                                        rotation: node.rotation(),
                                                        width: Math.max(40, sticker.width * scaleX), // Минимальный размер увеличен
                                                        height: Math.max(40, sticker.height * scaleX)
                                                    }
                                                }
                                            });
                                            node.scaleX(1);
                                            node.scaleY(1);
                                        }}
                                    />
                                ))}

                                <Transformer ref={transformerRef} />

                                {state.cropMode && (
                                    <>
                                        <Rect
                                            ref={cropRectRef}
                                            x={state.cropRect.x}
                                            y={state.cropRect.y}
                                            width={state.cropRect.width}
                                            height={state.cropRect.height}
                                            fill="rgba(0,0,0,0.3)"
                                            stroke="#1890ff"
                                            strokeWidth={2}
                                            dash={[5, 5]}
                                            draggable
                                            dragBoundFunc={(pos) => {
                                                let x = Math.max(0, pos.x);
                                                let y = Math.max(0, pos.y);

                                                x = Math.min(x, state.imageSize.width - state.cropRect.width);
                                                y = Math.min(y, state.imageSize.height - state.cropRect.height);

                                                return { x, y };
                                            }}
                                            onDragEnd={(e) => {
                                                const newRect = {
                                                    x: Math.max(0, Math.min(e.target.x(), state.imageSize.width - state.cropRect.width)),
                                                    y: Math.max(0, Math.min(e.target.y(), state.imageSize.height - state.cropRect.height)),
                                                    width: state.cropRect.width,
                                                    height: state.cropRect.height
                                                };
                                                dispatch({ type: 'UPDATE_CROP_RECT', payload: newRect });
                                            }}
                                        />
                                        <Transformer
                                            ref={cropTransformerRef}
                                            boundBoxFunc={(oldBox, newBox) => {
                                                if (newBox.width < 30 || newBox.height < 30) {
                                                    return oldBox;
                                                }
                                                if (newBox.x < 0) newBox.x = 0;
                                                if (newBox.y < 0) newBox.y = 0;
                                                if (newBox.x + newBox.width > state.imageSize.width) {
                                                    newBox.width = state.imageSize.width - newBox.x;
                                                }
                                                if (newBox.y + newBox.height > state.imageSize.height) {
                                                    newBox.height = state.imageSize.height - newBox.y;
                                                }
                                                if (state.cropAspect) {
                                                    const currentAspect = newBox.width / newBox.height;
                                                    if (Math.abs(currentAspect - state.cropAspect) > 0.01) {
                                                        if (currentAspect > state.cropAspect) {
                                                            newBox.width = newBox.height * state.cropAspect;
                                                        } else {
                                                            newBox.height = newBox.width / state.cropAspect;
                                                        }
                                                    }
                                                    if (newBox.x + newBox.width > state.imageSize.width) {
                                                        newBox.x = state.imageSize.width - newBox.width;
                                                    }
                                                    if (newBox.y + newBox.height > state.imageSize.height) {
                                                        newBox.y = state.imageSize.height - newBox.height;
                                                    }
                                                }
                                                return newBox;
                                            }}
                                            onTransformEnd={handleCropTransform}
                                        />
                                    </>
                                )}
                            </Layer>
                        </Stage>
                    )}
                </div>

                <div style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: '#1a1a1a'
                }}>
                    {mobileView === 'main' && (
                        <>
                            <MobileFiltersFullView />
                            <MobileToolsView />
                        </>
                    )}

                    {mobileView === 'adjust' && <MobileAdjustView />}
                    {mobileView === 'text' && <MobileTextView />}
                    {mobileView === 'crop' && <MobileCropView />}
                    {mobileView === 'stickers' && <MobileStickersView />}
                </div>
            </Modal>
            <style>{`
                @media (max-width: 768px) {
                    .mobile-text-optimized {
                    font-size: 24px !important;
                    scaleX: 1 !important;
                    scaleY: 1 !important;
                }
                
                .mobile-sticker-optimized {
                    scaleX: 1.5 !important;
                    scaleY: 1.5 !important;
                }
            }
            `}</style>
        </>
    );
};

const ControlsPanel = ({
    state,
    dispatch,
    onAddText,
    onAddSticker,
    onApplyCrop,
    onApplyFilter,
    onResetFilters,
    onApplyCropPreset,
    onApplySocialPreset,
    activeTab,
    setActiveTab
}) => {
    const { t } = useTranslation();
    const [textValue, setTextValue] = useState(t('imageEditor.text.placeholder'));
    const [textColor, setTextColor] = useState('#ffffff');
    const [fontSize, setFontSize] = useState(40);
    const [fontFamily, setFontFamily] = useState('Arial');
    const [textBold, setTextBold] = useState(false);
    const [textItalic, setTextItalic] = useState(false);
    const [textUnderline, setTextUnderline] = useState(false);

    const handleStickerUpload = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new window.Image();
            img.src = e.target.result;
            img.onload = () => onAddSticker(img);
        };
        reader.readAsDataURL(file);
        return false;
    };

    const handleAdjustmentChange = (key, value) => {
        dispatch({ type: 'SET_ADJUSTMENT', payload: { [key]: value } });
    };

    const FilterPreviewCard = React.memo(({
        filterKey,
        filter,
        isSelected,
        onApply,
        image,
        adjustments,
        rotation,
        imageSize
    }) => {
        const { t } = useTranslation();
        const canvasRef = useRef(null);
        const [previewUrl, setPreviewUrl] = useState(null);
        const [loading, setLoading] = useState(true);

        useEffect(() => {
            if (!image) {
                setPreviewUrl(null);
                setLoading(false);
                return;
            }

            const generatePreview = () => {
                const canvas = canvasRef.current || document.createElement('canvas');
                const ctx = canvas.getContext('2d', { alpha: false });

                const previewSize = 1000; 
                let drawWidth, drawHeight;

                const aspectRatio = imageSize.width / imageSize.height;

                if (aspectRatio > 1) {
                    drawWidth = previewSize;
                    drawHeight = previewSize / aspectRatio;
                } else {
                    drawHeight = previewSize;
                    drawWidth = previewSize * aspectRatio;
                }

                canvas.width = previewSize;
                canvas.height = previewSize;

                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, previewSize, previewSize);

                const x = (previewSize - drawWidth) / 2;
                const y = (previewSize - drawHeight) / 2;

                const filterString = `
                brightness(${(100 + adjustments.brightness) / 100})
                contrast(${(100 + adjustments.contrast) / 100})
                saturate(${(100 + adjustments.saturation) / 100})
                sepia(${adjustments.sepia}%)
                grayscale(${adjustments.grayscale}%)
                blur(${Math.max(0, adjustments.blur)}px)
                hue-rotate(${adjustments.hue}deg)
            `.trim();

                ctx.filter = filterString;

                ctx.save();
                ctx.translate(previewSize / 2, previewSize / 2);
                ctx.rotate((rotation * Math.PI) / 180);

                let finalDrawWidth = drawWidth;
                let finalDrawHeight = drawHeight;

                if (rotation === 90 || rotation === 270) {
                    finalDrawWidth = drawHeight;
                    finalDrawHeight = drawWidth;
                }

                ctx.drawImage(
                    image,
                    -finalDrawWidth / 2,
                    -finalDrawHeight / 2,
                    finalDrawWidth,
                    finalDrawHeight
                );

                ctx.restore();

                try {
                    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
                    setPreviewUrl(dataUrl);
                } catch (error) {
                    console.warn('Failed to generate filter preview:', error);
                    setPreviewUrl(null);
                } finally {
                    setLoading(false);
                }
            };

            const timeoutId = setTimeout(() => {
                if (image.complete && image.naturalWidth > 0) {
                    generatePreview();
                } else {
                    image.onload = generatePreview;
                    image.onerror = () => {
                        setLoading(false);
                        setPreviewUrl(null);
                    };
                }
            }, 100);

            return () => {
                clearTimeout(timeoutId);
                if (image) {
                    image.onload = null;
                    image.onerror = null;
                }
            };
        }, [image, adjustments, rotation, imageSize]);

        if (!canvasRef.current && typeof document !== 'undefined') {
            canvasRef.current = document.createElement('canvas');
        }

        const cardStyle = {
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            border: isSelected ? '2px solid #1890ff' : '1px solid #f0f0f0',
            borderRadius: '8px',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#fafafa'
        };

        return (
            <Card
                hoverable
                style={cardStyle}
                bodyStyle={{
                    padding: '0',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%'
                }}
                onClick={onApply}
            >
                <canvas ref={canvasRef} style={{ display: 'none' }} />

                {loading ? (
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5'
                    }}>
                        <Spin size="small" />
                    </div>
                ) : previewUrl ? (
                    <div style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <img
                            src={previewUrl}
                            alt={t(filter.nameKey)}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain', 
                                background: '#ffffff'
                            }}
                            onError={() => setPreviewUrl(null)}
                        />
                        <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            padding: '4px',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            fontSize: '10px',
                            textAlign: 'center',
                            fontWeight: '500'
                        }}>
                            {t(filter.nameKey)}
                        </div>
                    </div>
                ) : (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(45deg, #667eea, #764ba2)',
                            fontSize: '10px',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                        }}
                    >
                        <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            padding: '4px',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            fontSize: '10px',
                            textAlign: 'center',
                            fontWeight: '500'
                        }}>
                            {t(filter.nameKey)}
                        </div>
                        {t(filter.nameKey).slice(0, 2).toUpperCase()}
                    </div>
                )}

                {isSelected && (
                    <div style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#1890ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                        border: '2px solid #fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                        <CheckOutlined style={{ color: 'white', fontSize: '10px' }} />
                    </div>
                )}
            </Card>
        );
    });


    const cardStyle = {
        height: '100px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
    };

    const buttonStyle = {
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    };

    const tabs = [
        {
            key: 'social',
            label: <span><InstagramOutlined /> {t('imageEditor.social.title')}</span>,
            children: (
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <div className="p-4 bg-blue-50 rounded-lg mb-4">
                        <h4 className="font-bold mb-2">{t('imageEditor.social.instructionTitle')}</h4>
                        <p className="text-sm text-blue-800">
                            {t('imageEditor.social.instructionDescription')}
                        </p>
                    </div>

                    <Row gutter={[12, 12]}>
                        {Object.entries(SOCIAL_PRESETS).map(([key, preset]) => (
                            <Col span={12} key={key}>
                                <Card
                                    hoverable
                                    style={cardStyle}
                                    bodyStyle={{
                                        padding: '12px',
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    onClick={() => onApplySocialPreset(key)}
                                >
                                    <div className="text-2xl mb-2 text-blue-600" style={{ fontSize: '24px' }}>
                                        {preset.icon}
                                    </div>
                                    <div className="font-semibold text-sm mb-1 text-center">
                                        {t(preset.nameKey)}
                                    </div>
                                    <div className="text-xs text-gray-500 text-center">
                                        {preset.width} × {preset.height}
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            )
        },
        {
            key: 'crop',
            label: <span><ScissorOutlined /> {t('imageEditor.crop.title')}</span>,
            children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div className="bg-gray-50 p-3 rounded-lg mb-4">
                        <div className="flex justify-between items-center mb-2">
                            <span className="font-medium">{t('imageEditor.crop.originalSize')}</span>
                            <span className="text-sm text-gray-600">
                                {state.imageSize.width} × {state.imageSize.height}
                            </span>
                        </div>
                        {state.cropMode && (
                            <div className="flex justify-between items-center">
                                <span className="font-medium">{t('imageEditor.crop.cropSize')}</span>
                                <span className="text-sm text-blue-600 font-bold">
                                    {Math.round(state.cropRect.width)} × {Math.round(state.cropRect.height)}
                                </span>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block mb-3 font-medium">
                            {t('imageEditor.crop.presets')}
                        </label>
                        <Row gutter={[8, 8]}>
                            {Object.entries(CROP_PRESETS).map(([key, preset]) => (
                                <Col span={8} key={key}>
                                    <Card
                                        hoverable
                                        style={{
                                            ...cardStyle,
                                            height: '80px'
                                        }}
                                        bodyStyle={{
                                            padding: '8px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        onClick={() => onApplyCropPreset(key)}
                                    >
                                        <div className="text-sm font-medium text-center mb-1">
                                            {t(preset.nameKey)}
                                        </div>
                                        {preset.aspect && (
                                            <div className="text-xs text-gray-500 text-center">
                                                {preset.aspect.toFixed(2)}
                                            </div>
                                        )}
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: 16 }}>
                        <Button
                            type={state.cropMode ? 'primary' : 'default'}
                            onClick={() => dispatch({ type: 'TOGGLE_CROP_MODE' })}
                            style={{ ...buttonStyle, flex: 1 }}
                        >
                            {state.cropMode ? t('imageEditor.crop.exit') : t('imageEditor.crop.enter')}
                        </Button>
                        {state.cropMode && (
                            <Button
                                type="primary"
                                onClick={onApplyCrop}
                                style={{ ...buttonStyle, flex: 1 }}
                            >
                                {t('imageEditor.crop.apply')}
                            </Button>
                        )}
                    </div>
                </Space>
            )
        },
        {
            key: 'presets',
            label: <span><FilterOutlined /> {t('imageEditor.filters.title')}</span>,
            children: (
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <Row gutter={[8, 12]}>
                        {Object.entries(FILTER_PRESETS).map(([key, filter]) => (
                            <Col span={8} key={key}>
                                <FilterPreviewCard
                                    filterKey={key}
                                    filter={filter}
                                    isSelected={state.selectedFilter === key}
                                    onApply={() => onApplyFilter(key)}
                                    image={state.image}
                                    adjustments={filter}
                                    rotation={state.rotation}
                                    imageSize={state.imageSize}
                                />
                            </Col>
                        ))}
                    </Row>
                </div>
            )
        },
        {
            key: 'adjust',
            label: <span><BgColorsOutlined /> {t('imageEditor.adjust.title')}</span>,
            children: (
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {Object.entries(state.adjustments).map(([key, value]) => (
                            <div key={key} style={{ marginBottom: 16 }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 8
                                }}>
                                    <span style={{
                                        textTransform: 'capitalize',
                                        fontSize: '14px',
                                        fontWeight: 500
                                    }}>
                                        {t(`imageEditor.adjust.${key}`)}
                                    </span>
                                    <span style={{
                                        fontSize: '14px',
                                        color: '#1890ff',
                                        fontWeight: 500,
                                        minWidth: '40px',
                                        textAlign: 'right'
                                    }}>
                                        {value}
                                    </span>
                                </div>
                                <Slider
                                    min={key === 'contrast' ? -100 : 0}
                                    max={key === 'hue' ? 360 : (key === 'contrast' ? 100 : 200)}
                                    value={value}
                                    onChange={(val) => handleAdjustmentChange(key, val)}
                                    trackStyle={{ backgroundColor: '#1890ff' }}
                                    handleStyle={{ borderColor: '#1890ff' }}
                                />
                            </div>
                        ))}
                        <Button
                            onClick={onResetFilters}
                            danger
                            style={buttonStyle}
                        >
                            {t('imageEditor.adjust.reset')}
                        </Button>
                    </Space>
                </div>
            )
        },
        {
            key: 'text',
            label: <span><FontSizeOutlined /> {t('imageEditor.text.title')}</span>,
            children: (
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Input.TextArea
                            value={textValue}
                            onChange={e => setTextValue(e.target.value)}
                            placeholder={t('imageEditor.text.placeholder')}
                            rows={3}
                            style={{ marginBottom: 12 }}
                        />

                        <Select
                            value={fontFamily}
                            onChange={setFontFamily}
                            style={{ width: '100%', marginBottom: 12 }}
                            options={[
                                'Arial', 'Times New Roman', 'Courier New', 'Georgia',
                                'Verdana', 'Impact', 'Comic Sans MS', 'Trebuchet MS'
                            ].map(f => ({ value: f, label: f }))}
                        />

                        <div style={{ marginBottom: 12 }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: 8
                            }}>
                                <label style={{ fontSize: '14px', fontWeight: 500 }}>
                                    {t('imageEditor.text.size')}
                                </label>
                                <span style={{ color: '#1890ff', fontWeight: 500 }}>
                                    {fontSize}
                                </span>
                            </div>
                            <Slider
                                min={8}
                                max={128}
                                value={fontSize}
                                onChange={setFontSize}
                                trackStyle={{ backgroundColor: '#1890ff' }}
                                handleStyle={{ borderColor: '#1890ff' }}
                            />
                        </div>

                        <div style={{ marginBottom: 12 }}>
                            <ColorPicker
                                value={textColor}
                                onChange={c => setTextColor(c.toHexString())}
                                showText
                                style={{ width: '100%' }}
                            />
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '8px',
                            marginBottom: 16,
                            justifyContent: 'center'
                        }}>
                            <Button
                                type={textBold ? 'primary' : 'default'}
                                icon={<BoldOutlined />}
                                onClick={() => setTextBold(!textBold)}
                                style={{ flex: 1, ...buttonStyle }}
                            >
                                {t('imageEditor.text.bold')}
                            </Button>
                            <Button
                                type={textItalic ? 'primary' : 'default'}
                                icon={<ItalicOutlined />}
                                onClick={() => setTextItalic(!textItalic)}
                                style={{ flex: 1, ...buttonStyle }}
                            >
                                {t('imageEditor.text.italic')}
                            </Button>
                            <Button
                                type={textUnderline ? 'primary' : 'default'}
                                icon={<UnderlineOutlined />}
                                onClick={() => setTextUnderline(!textUnderline)}
                                style={{ flex: 1, ...buttonStyle }}
                            >
                                {t('imageEditor.text.underline')}
                            </Button>
                        </div>

                        <Button
                            type="primary"
                            onClick={() => onAddText({
                                text: textValue,
                                fill: textColor,
                                fontSize,
                                fontFamily,
                                fontStyle: `${textBold ? 'bold' : ''} ${textItalic ? 'italic' : ''}`.trim() || 'normal',
                                textDecoration: textUnderline ? 'underline' : 'none'
                            })}
                            style={buttonStyle}
                        >
                            {t('imageEditor.text.add')}
                        </Button>
                    </Space>
                </div>
            )
        },
        {
            key: 'stickers',
            label: <span><PictureOutlined /> {t('imageEditor.stickers.title')}</span>,
            children: (
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Upload beforeUpload={handleStickerUpload} showUploadList={false}>
                            <Button
                                block
                                icon={<PictureOutlined />}
                                style={buttonStyle}
                            >
                                {t('imageEditor.stickers.upload')}
                            </Button>
                        </Upload>

                        {state.stickers.length > 0 && (
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mt-4">
                                <p className="text-sm font-medium mb-2">
                                    {t('imageEditor.stickers.count', { count: state.stickers.length })}
                                </p>
                                <p className="text-xs text-gray-600">
                                    <Trans i18nKey="imageEditor.stickers.instructions">
                                        • Кликните на стикер для выбора<br />
                                        • Перетаскивайте для перемещения<br />
                                        • Выберите стикер и нажмите "Удалить" для удаления
                                    </Trans>
                                </p>
                            </div>
                        )}
                    </Space>
                </div>
            )
        }
    ];

    return (
        <div style={{
            width: '30%',
            height: '75vh',
            overflow: 'hidden',
            border: '1px solid #d9d9d9',
            borderRadius: '8px',
            backgroundColor: '#fff'
        }}>
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabs}
                style={{ height: '100%' }}
                tabPosition="top"
                size="small"
            />
        </div>
    );
}

export default KonvaImageEditorModal;